import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Alert,
  Menu,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  CheckCircle,
  Cancel,
  MoreVert,
  FilterList,
  Download,
  Person,
  CalendarMonth,
  Timeline,
  DragIndicator
} from '@mui/icons-material';
import moment from 'moment';
import AdminDragDropGrid from './AdminDragDropGrid';

const AdminScheduleManager = ({
  schedules = [],
  users = [],
  weekStartDate,
  onEdit,
  onDelete,
  onAdd,
  onApprove,
  onReject,
  onRefresh
}) => {
  const [viewMode, setViewMode] = useState('dragdrop'); // 'dragdrop', 'table' or 'calendar'
  const [filterUser, setFilterUser] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [weekDates, setWeekDates] = useState([]);
  
  // Form states
  const [editForm, setEditForm] = useState({
    userId: '',
    platform: '',
    date: '',
    startTime: '',
    endTime: '',
    status: 'pending',
    notes: ''
  });
  
  // Calculate week dates
  useEffect(() => {
    const dates = [];
    const startDate = moment(weekStartDate).startOf('week').add(1, 'day');
    
    for (let i = 0; i < 7; i++) {
      dates.push(startDate.clone().add(i, 'days'));
    }
    
    setWeekDates(dates);
  }, [weekStartDate]);
  
  // Filter schedules
  const filteredSchedules = schedules.filter(schedule => {
    if (filterUser !== 'all' && schedule.userId?._id !== filterUser) return false;
    if (filterPlatform !== 'all' && schedule.platform !== filterPlatform) return false;
    if (filterStatus !== 'all' && schedule.status !== filterStatus) return false;
    return true;
  });
  
  // Group schedules by user and day
  const groupSchedulesByUserAndDay = () => {
    const grouped = {};
    
    users.forEach(user => {
      grouped[user._id] = {
        user,
        days: weekDates.map(() => [])
      };
    });
    
    filteredSchedules.forEach(schedule => {
      const userId = schedule.userId?._id || schedule.userId;
      const scheduleDate = moment(schedule.date);
      
      weekDates.forEach((date, index) => {
        if (scheduleDate.isSame(date, 'day')) {
          if (grouped[userId]) {
            grouped[userId].days[index].push(schedule);
          }
        }
      });
    });
    
    return Object.values(grouped).filter(item => 
      item.days.some(day => day.length > 0)
    );
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'completed': return 'info';
      default: return 'warning';
    }
  };
  
  // Get platform color
  const getPlatformColor = (platform) => {
    return platform === 'shopee' ? '#EE4D2D' : '#000000';
  };
  
  // Handle edit click
  const handleEditClick = (schedule) => {
    setSelectedSchedule(schedule);
    setEditForm({
      userId: schedule.userId?._id || schedule.userId,
      platform: schedule.platform,
      date: moment(schedule.date).format('YYYY-MM-DD'),
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      status: schedule.status,
      notes: schedule.notes || ''
    });
    setEditDialogOpen(true);
  };
  
  // Handle edit submit
  const handleEditSubmit = async () => {
    if (onEdit && selectedSchedule) {
      await onEdit(selectedSchedule._id, editForm);
      setEditDialogOpen(false);
      setSelectedSchedule(null);
    }
  };
  
  // Handle delete
  const handleDelete = async (scheduleId) => {
    if (window.confirm('Bạn có chắc muốn xóa lịch này?')) {
      if (onDelete) {
        await onDelete(scheduleId);
      }
    }
  };
  
  // Calculate statistics
  const stats = {
    total: filteredSchedules.length,
    pending: filteredSchedules.filter(s => s.status === 'pending').length,
    approved: filteredSchedules.filter(s => s.status === 'approved').length,
    shopee: filteredSchedules.filter(s => s.platform === 'shopee').length,
    tiktok: filteredSchedules.filter(s => s.platform === 'tiktok').length,
    totalHours: filteredSchedules.reduce((sum, s) => {
      const [startH] = s.startTime.split(':').map(Number);
      const [endH] = s.endTime.split(':').map(Number);
      return sum + (endH - startH);
    }, 0)
  };
  
  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Tổng lịch
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Chờ duyệt
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.pending}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Đã duyệt
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.approved}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Tổng giờ
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.totalHours}h
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Tabs value={viewMode} onChange={(e, v) => setViewMode(v)}>
            <Tab 
              value="dragdrop" 
              label="Kéo thả (Drag & Drop)" 
              icon={<DragIndicator />} 
              iconPosition="start"
            />
            <Tab 
              value="table" 
              label="Danh sách" 
              icon={<Timeline />} 
              iconPosition="start"
            />
            <Tab 
              value="calendar" 
              label="Lịch tuần" 
              icon={<CalendarMonth />} 
              iconPosition="start"
            />
          </Tabs>
          
          <Box flex={1} />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Nhân viên</InputLabel>
            <Select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              label="Nhân viên"
            >
              <MenuItem value="all">Tất cả</MenuItem>
              {users.map(user => (
                <MenuItem key={user._id} value={user._id}>
                  {user.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Platform</InputLabel>
            <Select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              label="Platform"
            >
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="shopee">Shopee</MenuItem>
              <MenuItem value="tiktok">TikTok</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Trạng thái"
            >
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="pending">Chờ duyệt</MenuItem>
              <MenuItem value="approved">Đã duyệt</MenuItem>
              <MenuItem value="rejected">Từ chối</MenuItem>
              <MenuItem value="completed">Hoàn thành</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Tạo lịch mới cho bất kỳ nhân viên nào">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedSchedule(null);
                setEditForm({
                  userId: '',
                  platform: 'shopee',
                  date: weekDates[0]?.format('YYYY-MM-DD') || '',
                  startTime: '09:00',
                  endTime: '12:00',
                  status: 'approved',
                  notes: ''
                });
                setEditDialogOpen(true);
              }}
              sx={{ fontWeight: 'bold' }}
            >
              Thêm lịch cho nhân viên
            </Button>
          </Tooltip>
        </Stack>
      </Paper>
      
      {/* Drag & Drop View */}
      {viewMode === 'dragdrop' && (
        <AdminDragDropGrid
          schedules={filteredSchedules}
          users={users}
          weekDates={weekDates}
          onEdit={(scheduleId, schedule) => {
            setSelectedSchedule(schedule);
            setEditForm({
              userId: schedule.userId?._id || schedule.userId,
              platform: schedule.platform,
              date: moment(schedule.date).format('YYYY-MM-DD'),
              startTime: schedule.startTime,
              endTime: schedule.endTime,
              status: schedule.status,
              notes: schedule.notes || ''
            });
            setEditDialogOpen(true);
          }}
          onDelete={handleDelete}
          onAdd={(scheduleData) => {
            // Create new schedule
            if (onAdd) {
              onAdd(scheduleData);
            }
          }}
          onMove={(scheduleId, newData) => {
            // Move schedule to new slot
            const schedule = schedules.find(s => s._id === scheduleId);
            if (schedule && onEdit) {
              onEdit(scheduleId, { ...schedule, ...newData });
            }
          }}
        />
      )}
      
      {/* Table View */}
      {viewMode === 'table' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nhân viên</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Platform</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ngày</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Giờ</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Trạng thái</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ghi chú</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSchedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      Không có lịch nào
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSchedules.map((schedule) => (
                  <TableRow key={schedule._id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Person fontSize="small" />
                        <Typography variant="body2">
                          {schedule.userId?.fullName || 'N/A'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.platform?.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: getPlatformColor(schedule.platform),
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {moment(schedule.date).format('DD/MM/YYYY')}
                    </TableCell>
                    <TableCell>
                      {schedule.startTime} - {schedule.endTime}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          schedule.status === 'pending' ? 'Chờ duyệt' :
                          schedule.status === 'approved' ? 'Đã duyệt' :
                          schedule.status === 'rejected' ? 'Từ chối' : 'Hoàn thành'
                        }
                        size="small"
                        color={getStatusColor(schedule.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                        {schedule.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {schedule.status === 'pending' && (
                          <>
                            <Tooltip title="Duyệt">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => onApprove && onApprove(schedule._id)}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Từ chối">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onReject && onReject(schedule._id)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Sửa">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(schedule)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(schedule._id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            💡 <strong>Mẹo:</strong> Click vào bất kỳ slot nào (ô màu cam/đen) để chỉnh sửa nhanh. 
            Bạn có thể đổi giờ, platform, trạng thái hoặc xóa lịch.
          </Alert>
          
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 150 }}>
                    Nhân viên
                  </TableCell>
                {weekDates.map((date, index) => (
                  <TableCell key={index} sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                    {date.format('ddd')}
                    <br />
                    {date.format('DD/MM')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groupSchedulesByUserAndDay().map(({ user, days }) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {user.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.username}
                    </Typography>
                  </TableCell>
                  {days.map((daySchedules, dayIndex) => (
                    <TableCell key={dayIndex} sx={{ p: 0.5, verticalAlign: 'top' }}>
                      <Stack spacing={0.5}>
                        {daySchedules.map((schedule) => (
                          <Tooltip 
                            key={schedule._id}
                            title={`Click để sửa: ${schedule.startTime}-${schedule.endTime} (${schedule.platform})`}
                          >
                            <Paper
                              sx={{
                                p: 0.5,
                                bgcolor: getPlatformColor(schedule.platform),
                                color: 'white',
                                fontSize: '0.7rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.05)',
                                  boxShadow: 2
                                }
                              }}
                              onClick={() => handleEditClick(schedule)}
                            >
                              <Typography variant="caption" display="block" fontWeight="bold">
                                {schedule.startTime}-{schedule.endTime}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {schedule.platform === 'shopee' ? '🛒' : '🎵'}
                              </Typography>
                              <Chip
                                label={
                                  schedule.status === 'pending' ? 'Chờ' :
                                  schedule.status === 'approved' ? '✓' :
                                  schedule.status === 'rejected' ? '✗' : '●'
                                }
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.6rem',
                                  bgcolor: 'rgba(255,255,255,0.3)',
                                  color: 'white',
                                  mt: 0.25
                                }}
                              />
                            </Paper>
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSchedule ? 'Sửa lịch livestream' : 'Thêm lịch livestream'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Nhân viên</InputLabel>
              <Select
                value={editForm.userId}
                onChange={(e) => setEditForm({ ...editForm, userId: e.target.value })}
                label="Nhân viên"
              >
                {users.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.fullName} ({user.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={editForm.platform}
                onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })}
                label="Platform"
              >
                <MenuItem value="shopee">🛒 Shopee</MenuItem>
                <MenuItem value="tiktok">🎵 TikTok</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Ngày"
              type="date"
              value={editForm.date}
              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <Stack direction="row" spacing={2}>
              <TextField
                label="Giờ bắt đầu"
                type="time"
                value={editForm.startTime}
                onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Giờ kết thúc"
                type="time"
                value={editForm.endTime}
                onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            
            <FormControl fullWidth>
              <InputLabel>Trạng thái</InputLabel>
              <Select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                label="Trạng thái"
              >
                <MenuItem value="pending">Chờ duyệt</MenuItem>
                <MenuItem value="approved">Đã duyệt</MenuItem>
                <MenuItem value="rejected">Từ chối</MenuItem>
                <MenuItem value="completed">Hoàn thành</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Ghi chú"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Hủy
          </Button>
          <Button onClick={handleEditSubmit} variant="contained">
            {selectedSchedule ? 'Cập nhật' : 'Thêm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminScheduleManager;

